import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import { WebEDIAnnotation, WebEDIAnnotationCode } from 'services/webediErrors/types';
import { GenericError } from 'support/helpers/errors/errors';
import portalSchema from 'support/types/schema-portal';

import { normalizeAnnotationPath } from './normalizeAnnotationPath';
import { parseEphemeralPartnerIdentifiers } from './parseEphemeralPartnerIdentifiers';
import { remapAnnotationToFirstCell } from './remapAnnotationToFirstCell';
import { resolveAnnotationItemId } from './resolvers/resolveAnnotationItemId';

type Annotation = portalSchema.components['schemas']['Annotation'];
type UnmatchedArticleAnnotationValue = portalSchema.components['schemas']['UnmatchedArticleAnnotationValue'];

export type MapAnnotationContext = {
  canonical: Record<string, unknown> | undefined | null;
  flattenData: Record<string, unknown> | undefined;
  uiConfig: Array<ui.Section> | undefined;
};

export const mapAnnotation = (annotation: Annotation, ctx: MapAnnotationContext): WebEDIAnnotation | null => {
  if (annotation.resolved_at !== null) {
    return null;
  }

  const normalizedPath = normalizeAnnotationPath(annotation.path, ctx.canonical);
  if (typeof normalizedPath !== 'string') {
    logError(
      new GenericError('WebEDI: annotation path failed to normalize', {
        annotationId: annotation.id,
        path: annotation.path,
      }),
    );
    return null;
  }

  const annotationItemId = resolveAnnotationItemId(normalizedPath, { flattenData: ctx.flattenData });
  if (!annotationItemId) {
    logError(
      new GenericError('WebEDI: could not resolve annotation item id', {
        annotationId: annotation.id,
        path: annotation.path,
        normalizedPath,
      }),
    );
    return null;
  }

  const mappedItemId = Array.isArray(ctx.uiConfig)
    ? remapAnnotationToFirstCell(annotationItemId, ctx.uiConfig)
    : annotationItemId;
  if (!mappedItemId) {
    logError(
      new GenericError('WebEDI: annotation path is unreachable in uiConfig — dropping', {
        annotationId: annotation.id,
        path: annotation.path,
        normalizedPath,
        annotationItemId,
      }),
    );
    return null;
  }

  const mappedAnnotation =
    annotation.type === 'WARNING'
      ? mapWarningAnnotation(annotation, mappedItemId)
      : annotation.type === 'ERROR'
        ? mapErrorAnnotation(annotation, mappedItemId)
        : null;
  if (mappedAnnotation) {
    return mappedAnnotation;
  }

  logError(
    new GenericError('WebEDI: annotation has no mapper for (type, code) pair', {
      annotationId: annotation.id,
      type: annotation.type,
      code: annotation.value.code,
    }),
  );
  return null;
};

const getCurrentValueObject = (currentValue: unknown): Record<string, unknown> =>
  currentValue && typeof currentValue === 'object' ? (currentValue as Record<string, unknown>) : {};

const getTranslatedMessage = (currentValue: unknown): string => {
  const currentValueObject = getCurrentValueObject(currentValue);
  return typeof currentValueObject.translated_message === 'string' ? currentValueObject.translated_message : '';
};

const mapWarningAnnotation = (annotation: Annotation, fieldPath: string): WebEDIAnnotation | null => {
  switch (annotation.value.code) {
    case WebEDIAnnotationCode.UNMATCHED_ARTICLE: {
      const currentValueRaw = annotation.value.current_value;
      const currentValue: UnmatchedArticleAnnotationValue =
        currentValueRaw && typeof currentValueRaw === 'object'
          ? (currentValueRaw as UnmatchedArticleAnnotationValue)
          : { identifiers: {} };

      return {
        id: annotation.id,
        fieldPath,
        type: 'unmatched-article',
        annotationId: annotation.id,
        severity: 'warning',
        code: WebEDIAnnotationCode.UNMATCHED_ARTICLE,
        currentValue,
      };
    }
    case WebEDIAnnotationCode.EPHEMERAL_PARTNER: {
      const currentValueObject = getCurrentValueObject(annotation.value.current_value);
      const name = typeof currentValueObject.name === 'string' ? currentValueObject.name : '';
      const identifiers = parseEphemeralPartnerIdentifiers(currentValueObject.identifiers);
      return {
        id: annotation.id,
        fieldPath,
        type: 'ephemeral-partner',
        annotationId: annotation.id,
        severity: 'warning',
        code: WebEDIAnnotationCode.EPHEMERAL_PARTNER,
        currentValue: { name, identifiers },
      };
    }
    case WebEDIAnnotationCode.CONNECTOR_HOOK:
      return {
        id: annotation.id,
        fieldPath,
        type: 'connector-hook',
        annotationId: annotation.id,
        severity: 'warning',
        code: WebEDIAnnotationCode.CONNECTOR_HOOK,
        currentValue: { translatedMessage: getTranslatedMessage(annotation.value.current_value) },
      };
    case WebEDIAnnotationCode.OCR_VERIFICATION_WARNING:
      return {
        id: annotation.id,
        fieldPath,
        type: 'ocr-verification-warning',
        annotationId: annotation.id,
        severity: 'warning',
        code: WebEDIAnnotationCode.OCR_VERIFICATION_WARNING,
        currentValue: { translatedMessage: getTranslatedMessage(annotation.value.current_value) },
      };
    default:
      return null;
  }
};

const mapErrorAnnotation = (annotation: Annotation, fieldPath: string): WebEDIAnnotation | null => {
  if (annotation.value.code !== WebEDIAnnotationCode.CONNECTOR_HOOK) {
    return null;
  }
  return {
    id: annotation.id,
    fieldPath,
    type: 'connector-hook',
    annotationId: annotation.id,
    severity: 'error',
    code: WebEDIAnnotationCode.CONNECTOR_HOOK,
    currentValue: { translatedMessage: getTranslatedMessage(annotation.value.current_value) },
  };
};
